
import { useEffect, useCallback } from "react";
import { toast } from "sonner";
import { loadPermissions } from "../permissionService";
import { isLocalStorageAvailable } from "../utils/helpers";

/**
 * Hook for loading permissions data from storage/database
 */
export const usePermissionLoader = ({
  initialized,
  storageAvailable,
  initializationAttempts,
  databaseInitialized,
  setIsLoading,
  setLoadAttempts,
  setPermissions,
  setInitialPermissions,
  logPermissionsState
}) => {
  
  // Check localStorage availability
  useEffect(() => {
    const available = isLocalStorageAvailable();
    storageAvailable.current = available;
    
    console.log("[useRolePermissions] localStorage availability check:", available);
    
    if (!available) {
      console.warn("[useRolePermissions] localStorage is not available, will rely entirely on database");
    }
  }, [storageAvailable]);

  // Load permissions from database with retry mechanism and timestamp logging
  useEffect(() => {
    const initializePermissions = async () => {
      const timestamp = new Date().toISOString();
      console.log(`[${timestamp}] [useRolePermissions] Starting initialization (attempt ${initializationAttempts.current + 1})...`);
      
      if (initialized.current) {
        console.log(`[${timestamp}] [useRolePermissions] Already initialized, skipping`);
        return;
      }
      
      try {
        setIsLoading(true);
        setLoadAttempts(prev => prev + 1);
        
        // Log before loading
        console.log(`[${timestamp}] [useRolePermissions] About to load permissions from database...`);
        
        // Load permissions from database with robust error handling
        const loadedPermissions = await loadPermissions();
        databaseInitialized.current = true;
        
        console.log(`[${timestamp}] [useRolePermissions] Loaded permissions from database/storage:`, 
          loadedPermissions?.length || 0, "items");
        
        if (!Array.isArray(loadedPermissions) || loadedPermissions.length === 0) {
          throw new Error("Loaded permissions are not valid");
        }
        
        // Use structuredClone for maximum reliability of deep cloning
        console.log(`[${timestamp}] [useRolePermissions] Creating deep copies of loaded permissions`);
        const permissionsCopy = structuredClone(loadedPermissions);
        const initialCopy = structuredClone(loadedPermissions);
        
        // Log before state updates
        console.log(`[${timestamp}] [useRolePermissions] Setting state with loaded permissions`);
        
        setPermissions(permissionsCopy);
        setInitialPermissions(initialCopy);
        
        initialized.current = true;
        console.log(`[${timestamp}] [useRolePermissions] Successfully initialized permissions`);
        
        // Verify state setting was successful (setTimeout to ensure state updates have processed)
        setTimeout(() => {
          console.log(`[${timestamp}] [useRolePermissions] State update verification`);
          logPermissionsState();
        }, 100);
      } catch (error) {
        console.error(`[${timestamp}] [useRolePermissions] Error initializing permissions:`, error);
        
        // Increment the retry counter
        initializationAttempts.current += 1;
        
        // Retry initialization up to 3 times with increasing delays
        if (initializationAttempts.current < 3) {
          const retryDelay = initializationAttempts.current * 500; // 500ms, 1000ms, ...
          console.log(`[${timestamp}] [useRolePermissions] Retrying initialization in ${retryDelay}ms...`);
          
          setTimeout(() => {
            initializePermissions();
          }, retryDelay);
        } else {
          // After 3 failed attempts, show an error
          toast.error("Fout bij het laden van permissies, probeer de pagina te herladen");
          initialized.current = true; // Prevent further retries
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    // Start initialization process with a small delay to ensure component is fully mounted
    const timer = setTimeout(() => {
      console.log("[useRolePermissions] Delayed initialization starting");
      initializePermissions();
    }, 50);
    
    // Cleanup
    return () => {
      clearTimeout(timer);
      console.log("[useRolePermissions] Component unmounting, cleaned up initialization timer");
    };
  }, [
    initialized,
    initializationAttempts,
    databaseInitialized,
    setIsLoading,
    setLoadAttempts,
    setPermissions,
    setInitialPermissions,
    logPermissionsState
  ]);
};
